// import { create } from "axios";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useEffect, useState } from "react";
import ConfirmationModal1 from "components/ConfirmationModal1";
import { getEmployeesByAdminAndOrg } from "services/getEmployees";
import { createLegalAdvisoryRemark, createLegalAdvisoryProsecutorAssignment, createLegalRespondbackFeedback, updateLegalAdvisory, updateLegalAdvisoryProsecutorAssignment } from "services/legalAdvisoryService";
import { createLegislativeDraftingRemark, createLegislativeProsecutorAssignment, createLegislativeRespondbackFeedback, updateLegislativeDrafting, updateLegislativeProsecutorAssignment } from "services/legislativeService";
import { createSectorDirectiveProsecutorAssignment, createSectorDirectiveRemark, createSectorDirectiveRespondbackFeedback, deleteSectorDirectiveProsecutorAssignment, updateSectorDirective, updateSectorDirectiveProsecutorAssignment } from "services/sectorDirectives";
import { LoadingIcon } from "utils/constant";
import { FeedbackAllowedRoles, LEGAL_ADVISORY_REQUEST_STATUS, LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { getUserType } from "utils/helpers";
import { useTranslation } from "react-i18next";

interface TeamMember {
    id: number;
    name: string;
    role: string;
    isTeamLeader: boolean;
    prosecutor_name?: string;
    is_team_leader?: boolean;
}

// Helper function to generate initials
const getInitials = (name: string) => {
    if (!name) return "";
    const parts = name.split(" ");
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }
    return (
        parts[0].charAt(0).toUpperCase() +
        parts[parts.length - 1].charAt(0).toUpperCase()
    );
};

export default function DirectorRemark({ setEdit, setFormState, formState, type }: { setEdit: any, setFormState: any, formState: any, type: string }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();
    const contextData = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [forwardAction, setForwardAction] = useState<"coordinator" | "prosecutor">("coordinator");
    const [selectedProsecutor, setSelectedProsecutor] =
        useState<TeamMember | null>(null);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>(
        (formState.prosecutor_assignments ?? []).map((member: any) => ({
            ...member,
            isTeamLeader: member.isTeamLeader ?? member.is_team_leader ?? member.role === "leader" ? true : false,
            id: member.prosecutor_user_id,
            name: member.prosecutor_name,
            role: "Prosecutor"
        }))
    );
    const [employees, setEmployees] = useState([]);
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        getEmployeesByAdminAndOrg({
            bearer:
                contextData.token ??
                contextData?.user?.access_token ??
                contextData?.user.user?.access_token,
            org_unit: contextData?.user?.org_unit || "",
            admin_unit: contextData?.user?.admin_unit || "",
        })
            .then((data) => {
                setEmployees(
                    data
                        .filter((emp: any) =>
                            emp.roles?.some((role: string) => role === "LegalResearchProsecutor")
                        )
                        .map((emp: any) => ({
                            id: emp.user.id,
                            name: emp.user.first_name,
                            role: emp.roles[0] || "LegalResearchProsecutor",
                            isTeamLeader: false,
                        }))
                );
            })
            .catch((error) => {
                console.error("Error fetching:", error);
                showToast(t('FailedToFetchEmployees'), "error");
            });
    }, [getEmployeesByAdminAndOrg, setEmployees, t]);

    const handleChange =
        (
            e: React.ChangeEvent<
                HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
            >
        ) => {
            const { name, value } = e.target;
            setFormState((prev: any) => ({
                ...prev,
                [name]: value,
            }));
            console.log("check remark", formState.remark);
        };

    const handleReject = async () => {
        if (!formState.remark?.trim()) {
            showToast(t("PleaseWriteFeedbackBeforeRespondingBack") || "Please write your feedback before responding back.", "error");
            return;
        }
        setLoading(true);
        const remarkData = new FormData();
        if (type === "legal")
            remarkData.append("advisory_request", formState.id);
        else if (type === "sector_directives")
            remarkData.append("request", formState.id);
        else
            remarkData.append("instruction", formState.id);
        remarkData.append("role", getUserType(contextData.user?.roles[0]));
        remarkData.append("summary", formState.remark);
        remarkData.append("feedback_type", "Review");
        remarkData.append("given_by", contextData?.user?.full_name || "");

        try {
            let remark_response;
            if (type === "legal")
                remark_response = await createLegalRespondbackFeedback(remarkData);
            else if (type === "sector_directives")
                remark_response = await createSectorDirectiveRespondbackFeedback(remarkData, contextData.token || contextData?.user?.access_token || "");
            else
                remark_response = await createLegislativeRespondbackFeedback(remarkData);
            console.log(remark_response);
            const data = new FormData();
            // Respond Back returns the request to the requester with the review feedback.
            data.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.under_review.value);
            data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.under_review.value);
            data.append("forward_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.not_forwarded.value);
            let response: any;
            if (type === "legal") {
                response = await updateLegalAdvisory(formState.id, data);
            }
            else if (type === "sector_directives") {
                response = await updateSectorDirective(formState.id, data, contextData.token || contextData?.user?.access_token || "");
            }
            else {
                response = await updateLegislativeDrafting(formState.id, data);
            }

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t('LegislativeFeedbackSubmittedSuccess'), "success");
            setEdit(false);
            // Manually clear file inputs
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t('FailedToUpdateLegislativeDraftRequest'), "error");
        } finally {
            setLoading(false);
        }
    }

    const handleForward = async (froward_status: string) => {
        const remarkData = new FormData();
        if (type === "legal")
            remarkData.append("advisory_request", formState.id);
        else if (type === "sector_directives")
            remarkData.append("request", formState.id);
        else
            remarkData.append("instruction", formState.id);
        const feedbackRole = contextData?.user?.roles.find((role: string) => FeedbackAllowedRoles.has(role))
        if (feedbackRole)
            remarkData.append("role", getUserType(feedbackRole));
        remarkData.append("remark", formState.remark);

        try {
            let remark_response;
            if (type === "legal")
                remark_response = await createLegalAdvisoryRemark(remarkData);
            else if (type === "sector_directives")
                remark_response = await createSectorDirectiveRemark(remarkData, contextData.token || contextData?.user?.access_token || "");
            else
                remark_response = await createLegislativeDraftingRemark(remarkData);

            // If forwarding to prosecutor, save team members
            if (forwardAction === "prosecutor") {
                const currentIds = teamMembers.map((m) => m.id.toString());
                for (const prosecutor of formState.prosecutor_assignments || []) {
                    if (!currentIds.includes(prosecutor.prosecutor_user_id)) {
                        console.log("delete this prosecutor", prosecutor);
                        await deleteSectorDirectiveProsecutorAssignment(prosecutor.id, contextData.token || contextData?.user?.access_token || "");
                    }
                }
                for (const member of teamMembers) {
                    const prosecutorData = new FormData();
                    if (type === "legal")
                        prosecutorData.append("advisory_request", formState.id);
                    else if (type === "sector_directives")
                        prosecutorData.append("request", formState.id);
                    else
                        prosecutorData.append("instruction", formState.id);
                    prosecutorData.append("prosecutor_name", member.name);
                    prosecutorData.append("prosecutor_user_id", member.id.toString());
                    prosecutorData.append(
                        "role",
                        member.isTeamLeader ? "leader" : "member"
                    );
                    prosecutorData.append(
                        "is_team_leader",
                        member.isTeamLeader ? "true" : "false"
                    );
                    if (type === "legal")
                        if (formState.prosecutor_assignments?.find((prosecutor: any) => prosecutor.prosecutor_user_id === member.id.toString()))
                            await updateLegalAdvisoryProsecutorAssignment(
                                formState.prosecutor_assignments.find((prosecutor: any) => prosecutor.prosecutor_user_id === member.id.toString()).id, prosecutorData);
                        else
                            await createLegalAdvisoryProsecutorAssignment(prosecutorData);
                    else if (type === "sector_directives")
                        if (formState.prosecutor_assignments?.find((prosecutor: any) => prosecutor.prosecutor_user_id === member.id.toString()))
                            await updateSectorDirectiveProsecutorAssignment(
                                formState.prosecutor_assignments.find((prosecutor: any) => prosecutor.prosecutor_user_id === member.id.toString()).id, prosecutorData, contextData.token || contextData?.user?.access_token || "");
                        else
                            await createSectorDirectiveProsecutorAssignment(prosecutorData, contextData.token || contextData?.user?.access_token || "");
                    else
                        if (formState.prosecutor_assignments?.find((prosecutor: any) => prosecutor.prosecutor_user_id === member.id.toString()))
                            await updateLegislativeProsecutorAssignment(
                                formState.prosecutor_assignments.find((prosecutor: any) => prosecutor.prosecutor_user_id === member.id.toString()).id, prosecutorData);
                        else
                            await createLegislativeProsecutorAssignment(prosecutorData);
                }
            }

            const data = new FormData();
            data.append("status", LEGAL_ADVISORY_REQUEST_STATUS.director_remark_submitted.value);
            if (forwardAction === "prosecutor") {
                data.append("forward_status", LEGAL_ADVISORY_REQUEST_STATUS.assigned_to_prosecutor.value);
            } else {
                data.append("forward_status", LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value);
            }
            let response: any;
            if (type === "legal")
                response = await updateLegalAdvisory(formState.id, data);
            else if (type === "sector_directives")
                response = await updateSectorDirective(formState.id, data, contextData.token || contextData?.user?.access_token || "");
            else
                response = await updateLegislativeDrafting(formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t('LegislativeDraftForwardedSuccess'), "success");
            setEdit(false);
            // Manually clear file inputs
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t('FailedToUpdateLegislativeDraftRequest'), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveMember = (id: number) => {
        const memberToRemove = teamMembers.find((member) => member.id === id);
        if (
            memberToRemove?.isTeamLeader &&
            teamMembers.filter((m) => m.isTeamLeader).length === 1
        ) {
            setValidationError(
                t('CannotRemoveOnlyTeamLeader')
            );
            return;
        }
        setTeamMembers(teamMembers.filter((member) => member.id !== id));
        setValidationError(null);
    };

    const handleToggleTeamLeader = (id: number) => {
        setTeamMembers((prevMembers) =>
            prevMembers.map((member) => {
                if (member.id === id) {
                    return { ...member, isTeamLeader: !member.isTeamLeader };
                } else {
                    return { ...member, isTeamLeader: false };
                }
            })
        );
        setValidationError(null);
    };

    const handleAddProsecutor = () => {
        if (
            selectedProsecutor &&
            !teamMembers.some((member) => member.id === selectedProsecutor.id)
        ) {
            setTeamMembers((prevMembers) => [
                ...prevMembers,
                {
                    id: selectedProsecutor.id,
                    name: selectedProsecutor.name,
                    role: "Prosecutor",
                    isTeamLeader: prevMembers.length === 0, // First member added is the team leader
                },
            ]);
            setSelectedProsecutor(null);
            setValidationError(null);
        } else if (
            teamMembers.some((member) => member.id === selectedProsecutor?.id)
        ) {
            alert(t('ProsecutorAlreadyAdded'));
        }
    };

    const openModal = (action: "coordinator" | "prosecutor") => {
        if (action === "prosecutor" && teamMembers.length === 0) {
            showToast(t('PleaseAddTeamMemberBeforeForwarding'), "error");
            return;
        }
        setForwardAction(action);
        setIsModalOpen(true);
    };

    return (
        <>
            <div className="mt-6">
                <label
                    htmlFor="remark"
                    className="block text-sm font-medium text-gray-500"
                >
                    {t('Remark')}
                </label>
                <textarea
                    id="remark"
                    name="remark"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-24 resize-y"
                    placeholder={t('DetailHere')}
                    required
                    value={formState.remark}
                    onChange={handleChange}
                ></textarea>

                {/* Setup Team Members */}
                <div className="mt-6 mb-6">
                    <label
                        htmlFor="select-prosecutor"
                        className="block text-sm font-medium text-gray-700 mb-2"
                    >
                        {t('SetupTeamMembers')}
                    </label>
                    <div className="relative mb-3">
                        <select
                            id="select-prosecutor"
                            className="appearance-none w-full p-3 border border-gray-300 rounded-md bg-white pr-10 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                            value={selectedProsecutor?.id}
                            onChange={(e) =>
                                setSelectedProsecutor(
                                    employees.find(
                                        (employee: any) => employee.id === e.target.value
                                    ) ?? null
                                )
                            }
                        >
                            <option value="">{t('SelectProsecutor')}</option>
                            {employees.map((emp: any) => (
                                <option key={emp.id} value={emp.id}>
                                    {emp.name} - {emp.role}
                                </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-700">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleAddProsecutor}
                        className="mb-4 text-[#AA232F] hover:text-blue-800 text-sm"
                    >
                        {t('AddSelectedProsecutorToTeam')}
                    </button>

                    {validationError && (
                        <p className="text-red-500 text-sm mb-3">{validationError}</p>
                    )}

                    <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar scrollbar-hide">
                        {teamMembers.length === 0 && (
                            <p className="text-gray-500 text-sm italic">
                                {t('NoTeamMembersAddedYet')}
                            </p>
                        )}
                        {teamMembers.map((member) => (
                            <div
                                key={member.id}
                                className="flex items-center bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm"
                            >
                                <div className="w-10 h-10 rounded-full mr-3 bg-gray-700 text-white flex items-center justify-center font-bold text-sm select-none">
                                    {getInitials(member.name ?? member.prosecutor_name)}
                                </div>

                                <div className="flex-grow">
                                    <p className="font-semibold text-gray-800">
                                        {member.name ?? member.prosecutor_name}
                                    </p>
                                    <p className="text-sm text-gray-500">{member.role ?? t('Prosecutor')}</p>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id={`team-leader-${member.id}`}
                                        checked={member.isTeamLeader ?? member.role === "leader"}
                                        onChange={() => handleToggleTeamLeader(member.id)}
                                        className="accent-[#AA232F] form-checkbox h-4 w-4 text-gray-800 border-gray-300 rounded"
                                    />
                                    <label
                                        htmlFor={`team-leader-${member.id}`}
                                        className="text-sm text-gray-700"
                                    >
                                        {t('IsTeamLeader')}
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveMember(member.id)}
                                        className="text-gray-400 hover:text-red-500"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="#AA232F"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="mt-6 pb-8 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                    type="button"
                    onClick={handleReject}
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition-all disabled:opacity-50"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 015 5v2m-15-7l4-4m-4 4l4 4" />
                    </svg>
                    {t("RespondBack") || "Respond Back"}
                </button>
                <button
                    type="button"
                    onClick={() => openModal("coordinator")}
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-gray-100 text-gray-800 text-sm font-semibold hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition-all disabled:opacity-50 sm:ml-auto"
                >
                    {loading && forwardAction === "coordinator" ? (
                        <>{LoadingIcon} {t('Submitting')}...</>
                    ) : (
                        <>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                            {t('ForwardToCoordinator')}
                        </>
                    )}
                </button>
                <button
                    type="button"
                    onClick={() => openModal("prosecutor")}
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#0A1D39] text-white text-sm font-semibold shadow-sm hover:bg-[#13294d] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-2 transition-all disabled:opacity-50"
                >
                    {loading && forwardAction === "prosecutor" ? (
                        <>{LoadingIcon} {t('Submitting')}...</>
                    ) : (
                        <>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 100-8 4 4 0 000 8zm6 0a3 3 0 100-6M6 11a3 3 0 100-6" />
                            </svg>
                            {t('SetupTeamAndForwardToProsecutor')}
                        </>
                    )}
                </button>
            </div>
            <ConfirmationModal1
                isOpen={isModalOpen}
                isLoading={modalLoading}
                onCancel={() => setIsModalOpen(false)}
                onConfirm={async () => {
                    setModalLoading(true);
                    if (forwardAction === "prosecutor") {
                        await handleForward(LEGAL_ADVISORY_REQUEST_STATUS.assigned_to_prosecutor.value);
                    } else {
                        await handleForward(LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value);
                    }
                    setModalLoading(false);
                    setIsModalOpen(false);
                }}
                type="success"
            />
        </>
    );
}